import React, { useState, useEffect } from 'react';
import Login from './Login';
import { useAuth } from '../contexts/AuthContext';

// Icon components with proper TypeScript support
const MenuIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const MicIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const PsychologyIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const AssignmentIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const TrendingUpIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const SpeedIcon: React.FC<{ className?: string }> = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const LandingPage: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const { currentUser } = useAuth();

  // Auto-close login dialog when user is authenticated
  useEffect(() => {
    if (currentUser) {
      console.log('🟢 User authenticated in LandingPage, closing login dialog');
      setLoginOpen(false);
    }
  }, [currentUser]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLoginOpen = () => {
    setLoginOpen(true);
  };

  const handleLoginClose = () => {
    setLoginOpen(false);
  };

  const features = [
    {
      icon: <MicIcon />, 
      title: 'Voice-to-Text Magic',
      description: 'Transform your voice notes into organized, actionable tasks with advanced AI transcription technology.'
    },
    {
      icon: <PsychologyIcon />, 
      title: 'AI-Powered Organization',
      description: 'Our intelligent system automatically categorizes your thoughts using the proven Eisenhower Matrix framework.'
    },
    {
      icon: <AssignmentIcon />, 
      title: 'Smart Task Management',
      description: 'Convert scattered thoughts into prioritized action items that actually get done.'
    },
    {
      icon: <TrendingUpIcon />, 
      title: 'Productivity Insights',
      description: 'Track your progress and discover patterns in your thinking and task completion.'
    },
    {
      icon: <StarIcon />, 
      title: 'Seamless Experience',
      description: 'Beautiful, intuitive interface that works perfectly on all your devices.'
    },
    {
      icon: <SpeedIcon />, 
      title: 'Lightning Fast',
      description: 'Get from thought to organized task in seconds, not minutes.'
    }
  ];

  const drawer = (
    <div className="w-64 h-full bg-white dark:bg-slate-800 shadow-xl">
      <div className="p-4 flex justify-between items-center border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Menu</h3>
        <button
          onClick={handleDrawerToggle}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          <CloseIcon />
        </button>
      </div>
      <nav className="p-4 space-y-2">
        <button
          onClick={handleLoginOpen}
          className="w-full text-left px-4 py-3 rounded-lg hover:bg-accent-50 dark:hover:bg-accent-900/20 text-slate-700 dark:text-slate-300 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
        >
          Sign In
        </button>
        <button className="w-full text-left px-4 py-3 rounded-lg hover:bg-accent-50 dark:hover:bg-accent-900/20 text-slate-700 dark:text-slate-300 hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
          Features
        </button>
        <button className="w-full text-left px-4 py-3 rounded-lg hover:bg-accent-50 dark:hover:bg-accent-900/20 text-slate-700 dark:text-slate-300 hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
          About
        </button>
        <button className="w-full text-left px-4 py-3 rounded-lg hover:bg-accent-50 dark:hover:bg-accent-900/20 text-slate-700 dark:text-slate-300 hover:text-accent-600 dark:hover:text-accent-400 transition-colors">
          Contact
        </button>
      </nav>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900 dark:to-primary-800">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 glass border-b border-primary-200/20 dark:border-primary-700/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shadow-medium">
                <PsychologyIcon className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-accent-500 to-accent-600 bg-clip-text text-transparent">
                MindMatrix
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <button className="text-primary-700 hover:text-accent-600 px-3 py-2 text-sm font-medium transition-colors">
                Features
              </button>
              <button className="text-primary-700 hover:text-accent-600 px-3 py-2 text-sm font-medium transition-colors">
                About
              </button>
              <button className="text-primary-700 hover:text-accent-600 px-3 py-2 text-sm font-medium transition-colors">
                Contact
              </button>
              <button
                onClick={handleLoginOpen}
                className="btn-primary text-sm px-6 py-2"
              >
                Sign In
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={handleDrawerToggle}
              className="md:hidden p-2 rounded-lg hover:bg-accent-50 text-primary-700 transition-colors"
            >
              <MenuIcon />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDrawerToggle}></div>
          <div className="fixed right-0 top-0 h-full">
            {drawer}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="text-center mb-16">
          {/* Hero Badge */}
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-accent-50 border border-accent-200 text-accent-700 text-sm font-medium mb-8 animate-fade-in">
            ✨ New: AI-Powered Task Organization
          </div>
          
          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
            <span className="bg-gradient-to-r from-primary-800 via-primary-700 to-accent-600 bg-clip-text text-transparent">
              Transform Thoughts
            </span>
            <br />
            <span className="bg-gradient-to-r from-accent-500 to-accent-600 bg-clip-text text-transparent">
              into Action
            </span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg sm:text-xl md:text-2xl text-primary-600 dark:text-primary-400 max-w-3xl mx-auto mb-8 leading-relaxed">
            AI-powered voice note organization that automatically categorizes your thoughts 
            into actionable priorities using the proven Eisenhower Matrix.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={handleLoginOpen}
              className="btn-primary text-lg px-8 py-4 interactive shadow-strong"
            >
              Get Started Free
            </button>
            <button className="px-8 py-4 text-lg font-medium text-accent-600 border-2 border-accent-500 rounded-2xl hover:bg-accent-50 hover:border-accent-600 transition-all duration-200 interactive">
              Watch Demo
            </button>
          </div>
          
          {/* Stats or Social Proof */}
          <div className="grid grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold text-accent-600 mb-2">
                10K+
              </div>
              <div className="text-sm sm:text-base text-primary-600 dark:text-primary-400">
                Tasks Organized
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold text-accent-600 mb-2">
                99%
              </div>
              <div className="text-sm sm:text-base text-primary-600 dark:text-primary-400">
                Accuracy Rate
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl sm:text-4xl font-bold text-accent-600 mb-2">
                5★
              </div>
              <div className="text-sm sm:text-base text-primary-600 dark:text-primary-400">
                User Rating
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-6">
            Features
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-primary-800 to-primary-700 bg-clip-text text-transparent">
              Powerful Features
            </span>
          </h2>
          <p className="text-lg text-primary-600 dark:text-primary-400 max-w-2xl mx-auto mb-16">
            Everything you need to transform scattered thoughts into organized, actionable tasks
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group card p-8 text-center h-full flex flex-col interactive hover:shadow-strong hover:border-accent-200 hover:-translate-y-2 relative overflow-hidden"
            >
              {/* Gradient border on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-accent-100 to-accent-200 flex items-center justify-center text-accent-600 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-primary-800 dark:text-primary-200 mb-4">
                  {feature.title}
                </h3>
                <p className="text-primary-600 dark:text-primary-400 leading-relaxed flex-grow">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="glass rounded-3xl p-8 md:p-12 text-center mt-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent-50/30 to-accent-100/20 opacity-50" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="bg-gradient-to-r from-primary-800 to-accent-600 bg-clip-text text-transparent">
                Ready to Transform Your Productivity?
              </span>
            </h2>
            <p className="text-lg md:text-xl text-primary-600 dark:text-primary-400 max-w-2xl mx-auto mb-8">
              Join thousands of users who have revolutionized their task management with AI-powered voice organization.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={handleLoginOpen}
                className="btn-primary text-xl px-8 py-4 shadow-strong"
              >
                Start Your Journey
              </button>
              <button className="px-8 py-4 text-lg font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-2xl transition-colors">
                Learn More →
              </button>
            </div>
            
            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center gap-3 opacity-75">
              <span className="px-3 py-1 bg-white/50 border border-accent-200 rounded-full text-sm font-medium text-accent-700">
                🔒 Secure & Private
              </span>
              <span className="px-3 py-1 bg-white/50 border border-accent-200 rounded-full text-sm font-medium text-accent-700">
                ⚡ Free to Start
              </span>
              <span className="px-3 py-1 bg-white/50 border border-accent-200 rounded-full text-sm font-medium text-accent-700">
                🚀 No Credit Card
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-primary-900 text-primary-100 py-12 mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Logo and Description */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center mr-3">
                  <PsychologyIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white">MindMatrix</h3>
              </div>
              <p className="text-primary-300 max-w-sm">
                Transform your scattered thoughts into organized, actionable tasks with the power of AI.
              </p>
            </div>
            
            {/* Product Links */}
            <div className="text-center md:text-left">
              <h4 className="text-lg font-semibold text-white mb-4">Product</h4>
              <div className="space-y-2">
                <button className="block text-primary-300 hover:text-accent-400 transition-colors">Features</button>
                <button className="block text-primary-300 hover:text-accent-400 transition-colors">Pricing</button>
                <button className="block text-primary-300 hover:text-accent-400 transition-colors">API</button>
              </div>
            </div>
            
            {/* Company Links */}
            <div className="text-center md:text-left">
              <h4 className="text-lg font-semibold text-white mb-4">Company</h4>
              <div className="space-y-2">
                <button className="block text-primary-300 hover:text-accent-400 transition-colors">About</button>
                <button className="block text-primary-300 hover:text-accent-400 transition-colors">Contact</button>
                <button className="block text-primary-300 hover:text-accent-400 transition-colors">Privacy</button>
              </div>
            </div>
          </div>
          
          <div className="border-t border-primary-700 mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-primary-400 text-sm">
              © 2024 MindMatrix. All rights reserved.
            </p>
            <p className="text-primary-400 text-sm mt-2 sm:mt-0">
              Transform your thoughts into action.
            </p>
          </div>
        </div>
      </footer>

      {/* Login Dialog */}
      {loginOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleLoginClose}></div>
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-auto">
            <Login />
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
